import { useCallback, useState } from 'react';
import { useAuth } from '@/features/authentication';

interface UseHeaderResult {
  /** Whether the bell has anybody to fetch for; the bell fetches its own data. */
  notificationsEnabled: boolean;
  isUserMenuOpen: boolean;
  toggleUserMenu: () => void;
  closeUserMenu: () => void;
}

/**
 * The header's own state: who may see a notification feed, and whether the user menu is open.
 *
 * <p>Call this ONCE, from Header. niraxobid.md section 23.6 also calls it inside HeaderUserMenu, which
 * would give the two components independent state.
 *
 * <h2>The count moved out</h2>
 *
 * <p>This used to poll {@code /notifications/count} for a badge on a bell whose panel was deferred. The
 * panel exists now and the feed is real — {@code /notifications/in-app} — so the bell owns the counts, the
 * list and the marks, in {@code useNotifications}. Two owners of one badge is how the number and the list
 * come to disagree.
 */
export function useHeader(): UseHeaderResult {
  const { isAuthenticated } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen((previous) => !previous);
  }, []);

  const closeUserMenu = useCallback(() => {
    setIsUserMenuOpen(false);
  }, []);

  /*
    Everybody signed in has a feed.

    This was `isAdmin`, from when the only thing writing to the bell was a master import an Admin had
    started. The panel carries BID invitations, approval requests and award decisions now, and those are
    addressed to Initiators, Approvers and Suppliers -- the roles the old rule excluded. The server has
    always scoped the list to the signed-in user, so there was never anything for the role to protect:
    what the check actually did was hide notifications from the people they were written for.

    Still a flag rather than nothing, because the bell must not fetch before there is a token to fetch
    with: a request at that moment is a guaranteed 401 in every console on first paint.
  */
  return {
    notificationsEnabled: isAuthenticated,
    isUserMenuOpen,
    toggleUserMenu,
    closeUserMenu,
  };
}
